<script setup>
import OpcAddress from '@/components/basic/molecules/AddressForm.vue'
import { useAddressesStore } from '@/stores/addresses.ts'
import { useCustomerStore } from '@/stores/customer'
import { useNewCheckoutStore } from '@/stores/newCheckout.ts'
import { useOpcStore } from '@/stores/opc'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRaw,
  watch
} from 'vue'
import { useDisplay } from 'vuetify'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const TheOpcSummary = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcSummary.vue')
)
const NewPaymentMethodsList = defineAsyncComponent(
  () => import('@/components/opc/organisms/NewPaymentMethodsList.vue')
)
const TheOpcQuantitySelector = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcQuantitySelector.vue')
)
const AgeCheck = defineAsyncComponent(
  () => import('@/components/basic/molecules/AgeCheck.vue')
)
const TheOpcCouponStep = defineAsyncComponent(
  () => import('@/components/opc/atoms/TheOpcCouponStep.vue')
)
const OpcEmail = defineAsyncComponent(
  () => import('@/components/opc/molecules/OpcEmail.vue')
)
const FadeHeight = defineAsyncComponent(
  () => import('@/components/transitions/FadeHeight.vue')
)
const ProgressStep = defineAsyncComponent(
  () => import('@/components/opc/atoms/ProgressStep.vue')
)
const TheOpcStepper = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcStepper.vue')
)
const TheOpcBundleItems = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcBundleItems.vue')
)

const SCROLL_OFFSET = 122

const opcStore = useOpcStore()
const newCheckoutStore = useNewCheckoutStore()
const customerStore = useCustomerStore()
const addressesStore = useAddressesStore()
const { mdAndDown } = useDisplay()

const customerDataFetched = ref(false)

// Get checkout data from opcStore (refactored)
const {
  selectedShippingAddressData,
  selectedBillingAddressData,
  selectedPaymentMethodData,
  email,
  useSameBillingAddress,
  ageData
} = storeToRefs(opcStore)

const { params, product, updating, selectedQty, grandTotal, calculated } =
  storeToRefs(opcStore)
const { customerProfile, customerActive } = storeToRefs(customerStore)

const step = ref(1)
const products = ref([])
const itemQuantities = ref([])

const isBundle = computed(() => calculated.value.isBundle)
const showCouponStep = computed(() => params.value?.showCouponStep ?? false)
const trackingCouponCode = computed(() => params.value?.couponCode ?? null)
const dob = computed(() => customerProfile.value?.dob)
const elbCheckoutData = computed(
  () => `qty:${selectedQty.value};grand_total:${grandTotal.value}`
)

const updateData = (data) => {
  selectedPaymentMethodData.value = data
}

const updateAge = (age) => {
  opcStore.ageData = age
}

const _getModules = (() => {
  let cache
  return async () => {
    if (cache) return cache
    const [
      { elb },
      { WALKER_COMMANDS },
      { default: ApiController },
      { PDP_CONFIG }
    ] = await Promise.all([
      await import('@elbwalker/walker.js'),
      await import('@/services/helpers/ElbwalkerHelper'),
      await import('@/services/ApiController'),
      await import('@/services/helpers/ProductHelper')
    ])
    cache = { elb, WALKER_COMMANDS, ApiController, PDP_CONFIG }
    return cache
  }
})()

const handleScroll = () => {
  const el = document.getElementById('opc-body')
  if (!el) return

  const sections = [
    { selector: '#opc-summary', stepValue: 3 },
    { selector: '#opc-payment-methods', stepValue: 2 },
    { selector: '#opc-shipping-address', stepValue: 1 },
    { selector: '#opc-qty-selector', stepValue: 0 }
  ]

  for (const { selector, stepValue } of sections) {
    const element = el.querySelector(selector)
    if (element && SCROLL_OFFSET >= element.getBoundingClientRect().top) {
      step.value = stepValue
      return
    }
  }
  step.value = 0
}

async function loadBundleItems() {
  const { ApiController } = await _getModules()
  if (product.value?.bundle_items) {
    products.value = await Promise.all(
      product.value.bundle_items.map(async (item) => {
        const productData = await ApiController.fetchProductForDefaultCampaign(
          item.product_id
        )
        return productData
      })
    )
    itemQuantities.value = product.value.bundle_items.map((item) => item.qty)
  }
}

onMounted(async () => {
  await addressesStore.fetch()

  selectedShippingAddressData.value =
    addressesStore.getDefaultShippingAddress(true)

  selectedBillingAddressData.value = useSameBillingAddress.value
    ? null
    : addressesStore.getDefaultBillingAddress(true)

  customerDataFetched.value = true

  await nextTick()

  const updateScrollListener = () => {
    window.removeEventListener('scroll', handleScroll)
    if (mdAndDown.value) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      setTimeout(handleScroll, 100)
    }
  }

  updateScrollListener()
  watch(mdAndDown, updateScrollListener)
})

watch(product, async () => {
  await nextTick()
  await loadBundleItems()
  setTimeout(async () => {
    const { elb, WALKER_COMMANDS } = await _getModules()
    elb(WALKER_COMMANDS.init, document.getElementById('opc-body'))
  }, 0)
})

const shippingAddressFormRef = ref(null)
const billingAddressFormRef = ref(null)
const emailFormRef = ref(null)
const ageFormRef = ref(null)
const paymentFormRef = ref(null)
const couponStepRef = ref(null)

watch(
  () => useSameBillingAddress.value,
  (newValue) => {
    if (newValue) {
      selectedBillingAddressData.value = null
    } else {
      selectedBillingAddressData.value =
        addressesStore.getDefaultBillingAddress(true) ??
        addressesStore.getEmptyAddress()
    }
  }
)

function useFormWatcher(refVar, formName) {
  watch(
    () => refVar.value,
    (newValue) => {
      newValue
        ? newCheckoutStore.registerForm(formName, newValue)
        : newCheckoutStore.unregisterForm(formName)
    }
  )
}

useFormWatcher(shippingAddressFormRef, 'shippingAddress')
useFormWatcher(billingAddressFormRef, 'billingAddress')
useFormWatcher(emailFormRef, 'emailFormRef')
useFormWatcher(ageFormRef, 'ageFormRef')
useFormWatcher(paymentFormRef, 'paymentForm')
useFormWatcher(couponStepRef, 'couponStep')

onUnmounted(() => {
  newCheckoutStore.unregisterForm('shippingAddress')
  newCheckoutStore.unregisterForm('billingAddress')
  newCheckoutStore.unregisterForm('emailFormRef')
  newCheckoutStore.unregisterForm('ageFormRef')
  newCheckoutStore.unregisterForm('paymentForm')
  newCheckoutStore.unregisterForm('couponStep')

  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div
    id="opc-body"
    class="v-container pt-lg-5 pb-5"
    data-elb="checkout"
    data-elbcontext="shopping:checkout"
    data-elbaction="load:view(checkout)"
    :data-elb-checkout="elbCheckoutData"
  >
    <div class="v-row">
      <TheOpcStepper
        v-if="$vuetify.display.mdAndDown"
        class="stepper w-100 p-1 p-sm-3 position-sticky top-0 my-3"
        :initial-step="step + 1"
      />
      <div class="v-col-12 v-col-lg-8">
        <div class="v-row">
          <div
            v-if="$vuetify.display.lgAndUp"
            class="v-col-2 d-none d-lg-flex flex-column justify-start align-center text-center mt-3"
          >
            <span class="text-uppercase font-size-s l-spacing-half">{{
              translate('opc_amount')
            }}</span>
            <LazyComponent id="opc-stepper" :delay="1000">
              <ProgressStep class="mt-2" step="1" max="4" />
            </LazyComponent>
          </div>
          <VSheet class="v-col-12 v-col-lg-10 pa-3 pt-5">
            <TheOpcQuantitySelector id="opc-qty-selector" />
          </VSheet>
        </div>
        <div class="v-row">
          <div
            v-if="$vuetify.display.lgAndUp"
            class="v-col-2 d-none d-lg-flex flex-column justify-start align-center text-center mt-3"
          >
            <span class="text-uppercase font-size-s l-spacing-half">{{
              translate('opc_step_two_title')
            }}</span>
            <ProgressStep class="mt-2" step="2" max="4" />
          </div>
          <VSheet class="v-col-12 v-col-lg-10 pa-3">
            <OpcAddress
              v-if="customerDataFetched"
              ref="shippingAddressFormRef"
              id="opc-shipping-address"
              customer-property="shippingAddress"
              :initial-address="toRaw(selectedShippingAddressData)"
              @update-data="selectedShippingAddressData = $event"
            />
            <div class="v-row">
              <div class="v-col-12">
                <VCheckbox
                  v-model="useSameBillingAddress"
                  :label="translate('checkout.use_same_billing_address')"
                />
              </div>
            </div>
            <FadeHeight>
              <OpcAddress
                v-if="!useSameBillingAddress && customerDataFetched"
                ref="billingAddressFormRef"
                customer-property="billingAddress"
                :initial-address="selectedBillingAddressData"
                @update-data="selectedBillingAddressData = $event"
              />
            </FadeHeight>
            <div v-show="!customerActive || !dob">
              <div class="divider mb-5"></div>
              <OpcEmail
                ref="emailFormRef"
                v-show="!customerActive"
                customer-property="email"
                v-model="email"
              />
              <AgeCheck
                ref="ageFormRef"
                v-if="!dob"
                id="opc-shipping-age"
                customer-property="shippingAge"
                required
                @update="updateAge"
              />
            </div>
          </VSheet>
        </div>
        <div class="v-row">
          <div
            v-if="$vuetify.display.lgAndUp"
            class="v-col-2 d-none d-lg-flex flex-column justify-start align-center text-center mt-3"
          >
            <span class="text-uppercase font-size-s l-spacing-half">{{
              translate('payment')
            }}</span>
            <ProgressStep class="mt-2" step="3" max="4" />
          </div>
          <VSheet class="v-col-12 v-col-lg-10 pt-4 overflow-hidden">
            <NewPaymentMethodsList
              ref="paymentFormRef"
              id="opc-payment-methods"
              @update-data="updateData"
            />
            <TheOpcCouponStep
              ref="couponStepRef"
              v-if="showCouponStep"
              id="opc-coupon"
              :initial-tracking-coupon-code="trackingCouponCode"
              class="mt-5"
            />
          </VSheet>
        </div>
        <div class="v-row mt-3" v-if="$vuetify.display.lgAndUp">
          <div class="v-col-12 v-col-lg-10 offset-lg-2 px-0">
            <LazyComponent id="opc-bundle-items" :delay="1000">
              <TheOpcBundleItems
                :itemQuantities="isBundle ? itemQuantities : [1]"
                :products="isBundle ? products : [product]"
                :loading="updating"
              />
            </LazyComponent>
          </div>
        </div>
      </div>
      <div class="v-col-12 v-col-lg-4">
        <div id="opc-summary" class="v-row pl-lg-3 position-sticky top-0">
          <TheOpcSummary class="v-col-12" customer-property="agreements" />
        </div>
      </div>
      <div class="mt-3" v-if="$vuetify.display.mdAndDown">
        <TheOpcBundleItems
          :itemQuantities="isBundle ? itemQuantities : [1]"
          :products="isBundle ? products : [product]"
          :loading="updating"
          class="v-col-12"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.stepper {
  z-index: 2;
}

.display-sm-and-up .stepper {
  top: 20px;
}

.divider {
  border-bottom: 1px solid rgb(var(--v-theme-beige));
  width: 100%;
}
</style>
